import { useState, useEffect, type ReactNode } from 'react'
import {
  LinkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'
import { HoverHelp } from '@/tableControls'
import {
  getQBOTokenStatus,
  disconnectQBO,
  syncCache,
  syncCategoryHistory,
  refreshModelSheet,
  refreshQboStoreLists,
} from '@/services/qbo'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'

/** Keep in sync with services/qbo_sync_config.py (category history "Update mappings"). */
const CATEGORY_HISTORY_SYNC_ANCHOR_ISO = '2025-01-01'
const CATEGORY_HISTORY_SYNC_MAX_DAYS = 730

const settingsSubsectionTitleClass =
  'container-title leading-snug tracking-tight'

interface QBOStatus {
  valid: boolean
  realm_id?: string | null
  error?: string | null
}

function SettingsSubsectionHeading({ title }: { title: string }) {
  return (
    <div className="mb-4">
      <h3 className={settingsSubsectionTitleClass}>{title}</h3>
    </div>
  )
}

function SettingsSection({
  title,
  detail,
  icon,
  children,
}: {
  title: string
  detail?: ReactNode
  icon?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-950/[0.04]">
      <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50/90 to-gray-50/30 px-6 py-4">
        <div className="flex items-center gap-3">
          {icon ? <div className="flex-shrink-0">{icon}</div> : null}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {detail ? (
              <h2 className="text-base font-semibold leading-snug text-gray-900 tracking-tight">
                <HoverHelp label={title} aria-label={`Learn more: ${title}`}>
                  {detail}
                </HoverHelp>
              </h2>
            ) : (
              <h2 className="text-base font-semibold leading-snug text-gray-900 tracking-tight">{title}</h2>
            )}
          </div>
        </div>
      </div>
      <div className="p-6 sm:p-7 lg:p-8">{children}</div>
    </section>
  )
}

export default function QuickBooksIntegrationSection() {
  const [qboStatus, setQboStatus] = useState<QBOStatus | null>(null)
  const [qboLoading, setQboLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncingCategories, setSyncingCategories] = useState(false)
  const [refreshingModelSheet, setRefreshingModelSheet] = useState(false)
  const [categoryMappingModalOpen, setCategoryMappingModalOpen] = useState(false)
  const [categoryMappingFullResync, setCategoryMappingFullResync] = useState(false)

  const startSynthetic = useBackgroundJobsStore((s) => s.startSynthetic)
  const completeSynthetic = useBackgroundJobsStore((s) => s.completeSynthetic)
  const failSynthetic = useBackgroundJobsStore((s) => s.failSynthetic)

  useEffect(() => {
    void loadQBOStatus()
  }, [])

  useEffect(() => {
    if (!categoryMappingModalOpen || syncingCategories) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCategoryMappingModalOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [categoryMappingModalOpen, syncingCategories])

  async function loadQBOStatus() {
    try {
      setQboLoading(true)
      const status = await getQBOTokenStatus()
      setQboStatus(status)
    } catch (err) {
      console.error('Failed to load QBO status:', err)
      setQboStatus({ valid: false, error: 'Failed to check status' })
    } finally {
      setQboLoading(false)
    }
  }

  async function handleDisconnect() {
    if (!confirm('Are you sure you want to disconnect from QuickBooks Online?')) {
      return
    }
    try {
      setDisconnecting(true)
      await disconnectQBO()
      setQboStatus({ valid: false })
      toast.success('Disconnected from QuickBooks Online')
    } catch (err) {
      console.error('Failed to disconnect:', err)
      toast.error('Failed to disconnect from QuickBooks')
    } finally {
      setDisconnecting(false)
    }
  }

  async function handleSyncCache() {
    const toastId = startSynthetic('Syncing QBO data…')
    try {
      setSyncing(true)
      const result = await syncCache()
      await refreshQboStoreLists(true)
      const msg = `Synced ${result.synced.vendors} vendors, ${result.synced.locations} locations, ${result.synced.categories} categories`
      completeSynthetic(toastId, msg)
    } catch (err) {
      console.error('Failed to sync:', err)
      failSynthetic(toastId, 'Failed to sync QBO data')
    } finally {
      setSyncing(false)
    }
  }

  async function handleRefreshModelSheet() {
    const toastId = startSynthetic('Refreshing QBO Google Sheet…')
    try {
      setRefreshingModelSheet(true)
      const result = await refreshModelSheet()
      const msg = result.message || 'QBO Google Sheet refresh completed'
      completeSynthetic(toastId, msg)
    } catch (err) {
      console.error('Failed to refresh model sheet:', err)
      failSynthetic(toastId, 'Failed to refresh QBO Google Sheet')
    } finally {
      setRefreshingModelSheet(false)
    }
  }

  function openCategoryMappingModal() {
    setCategoryMappingFullResync(false)
    setCategoryMappingModalOpen(true)
  }

  async function confirmCategoryMappingSync() {
    const toastId = startSynthetic(
      categoryMappingFullResync
        ? 'Rebuilding category mappings from QuickBooks…'
        : 'Updating category mappings from QuickBooks…'
    )
    try {
      setSyncingCategories(true)
      const startDate = new Date(CATEGORY_HISTORY_SYNC_ANCHOR_ISO)
      const today = new Date()
      const rawDays = Math.ceil(
        (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      )
      const days = Math.min(
        CATEGORY_HISTORY_SYNC_MAX_DAYS,
        Math.max(1, rawDays)
      )
      const result = await syncCategoryHistory(days, {
        fullResync: categoryMappingFullResync,
      })
      const { details } = result
      const totalTxns = details.bills_processed + details.credits_processed
      let msg = `Synced ${totalTxns} transactions, recorded ${details.line_items_recorded} category mappings`
      if (categoryMappingFullResync) {
        msg += ' (full rebuild)'
      }
      completeSynthetic(toastId, msg)
      setCategoryMappingModalOpen(false)
    } catch (err) {
      console.error('Failed to sync category history:', err)
      failSynthetic(toastId, 'Failed to sync category history from QBO')
    } finally {
      setSyncingCategories(false)
    }
  }

  function handleConnect() {
    window.location.href = '/api/qbo/authorize'
  }

  const qboSectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100/90 ring-1 ring-emerald-200/50">
      <BuildingOffice2Icon className="h-5 w-5 text-emerald-700" />
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      <SettingsSection
        title="QuickBooks integration"
        icon={qboSectionIcon}
        detail={
          <>
            <p>
              Link your QuickBooks Online company so Bookkeeper can load vendors, locations, and expense
              categories; create bills from approved documents; and (optionally) learn category patterns from
              bills and vendor credits you have already booked.
            </p>
            <p>
              Disconnecting only removes the OAuth link—category-mapping history stays in this app until you
              change or clear it.
            </p>
          </>
        }
      >
        {qboLoading ? (
          <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm text-gray-500">
            <ArrowPathIcon className="w-5 h-5 animate-spin flex-shrink-0" />
            Checking connection…
          </div>
        ) : qboStatus?.valid ? (
          <div className="space-y-8">
            <div className="flex flex-col gap-3 border-b border-gray-100 pb-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Connected to QuickBooks</p>
                  <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                </div>
                {qboStatus.realm_id ? (
                  <p className="mt-1 text-sm text-gray-500">
                    Company ID{' '}
                    <code className="break-all rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-800">
                      {qboStatus.realm_id}
                    </code>
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">Company is linked.</p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void loadQBOStatus()}
                  className="btn btn-secondary shrink-0 text-sm"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => void handleDisconnect()}
                  disabled={disconnecting}
                  className="btn btn-secondary shrink-0 text-sm text-red-600 hover:bg-red-50"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>

            <div>
              <SettingsSubsectionHeading title="Sync from QuickBooks" />
              <div className="divide-y divide-gray-100">
                <div className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Sync QBO data</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Syncs vendors, locations, and expense categories from QuickBooks into Bookkeeper.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleSyncCache()}
                    disabled={syncing}
                    className="btn btn-secondary shrink-0 text-sm"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing…' : 'Sync'}
                  </button>
                </div>
                <div className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Update mappings</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Rebuilds category suggestions from recent QuickBooks bills and vendor credits.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openCategoryMappingModal}
                    disabled={syncingCategories}
                    className="btn btn-secondary shrink-0 text-sm"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${syncingCategories ? 'animate-spin' : ''}`} />
                    {syncingCategories ? 'Updating…' : 'Update'}
                  </button>
                </div>
                <div className="flex flex-col gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">Refresh QBO Google Sheet</p>
                    <p className="mt-1 text-sm text-gray-500">
                      Updates the Cottage Grocery model Google Sheet from Bookkeeper&apos;s
                      QuickBooks connection (same tabs the overnight Apps Script updates).
                      May take several minutes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRefreshModelSheet()}
                    disabled={refreshingModelSheet}
                    className="btn btn-secondary shrink-0 text-sm"
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 ${refreshingModelSheet ? 'animate-spin' : ''}`}
                    />
                    {refreshingModelSheet ? 'Updating…' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 px-5 py-4">
              <div className="flex gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-950">Not connected</p>
                  <p className="text-sm text-amber-900/75 mt-1">
                    Connect to pull lists, ingest from email rules, post bills, and use category tools.
                  </p>
                  {qboStatus?.error ? (
                    <p className="mt-3 max-w-full break-words rounded-md bg-white/70 px-3 py-2 font-mono text-xs text-amber-900 ring-1 ring-amber-100">
                      {qboStatus.error}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            <button type="button" onClick={handleConnect} className="btn btn-primary">
              <LinkIcon className="w-4 h-4" />
              Connect to QuickBooks
            </button>
          </div>
        )}
      </SettingsSection>

      {categoryMappingModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center bg-slate-900/40 backdrop-blur-[2px] p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="category-mapping-modal-title"
          onClick={() => !syncingCategories && setCategoryMappingModalOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl ring-1 ring-black/5 max-w-lg w-full max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    id="category-mapping-modal-title"
                    className="text-lg font-semibold text-gray-900 tracking-tight"
                  >
                    Update category mappings
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 leading-snug">
                    Reads QuickBooks bills and vendor credits from January 2025 through today (capped at{' '}
                    {CATEGORY_HISTORY_SYNC_MAX_DAYS} days) to refresh how we map vendors and line text to expense
                    categories. Pick incremental update or a full rebuild from your books.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => !syncingCategories && setCategoryMappingModalOpen(false)}
                  className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-40"
                  disabled={syncingCategories}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-5 py-2.5 text-xs text-gray-600 leading-relaxed">
              <QuestionMarkCircleIcon className="w-4 h-4 shrink-0 text-gray-400 mt-0.5" aria-hidden="true" />
              <span>
                Leave <strong className="font-semibold text-gray-800">Full rebuild</strong> unchecked for a normal
                run: only bills and credits this app has not recorded yet are appended to mapping history.
              </span>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div
                className={`rounded-xl border p-4 transition-colors ${
                  categoryMappingFullResync
                    ? 'border-amber-200/90 bg-amber-50/40 shadow-sm shadow-amber-900/5'
                    : 'border-gray-200/90 bg-gray-50/40'
                }`}
              >
                <div className="flex gap-3">
                  <input
                    id="settings-category-mapping-full-resync"
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-amber-600 focus:ring-amber-500 focus:ring-offset-0"
                    checked={categoryMappingFullResync}
                    onChange={(e) => setCategoryMappingFullResync(e.target.checked)}
                    disabled={syncingCategories}
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <label
                      htmlFor="settings-category-mapping-full-resync"
                      className="text-sm font-semibold text-gray-900 cursor-pointer"
                    >
                      Full rebuild from QuickBooks
                    </label>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Deletes locally stored mapping history, then re-imports from QuickBooks for the window above.
                      Use after you recategorize existing transactions in QuickBooks so suggestions match your
                      current books. Expect a longer run and a live QuickBooks connection.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-gradient-to-b from-gray-50/30 to-gray-50 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                className="btn btn-secondary w-full sm:w-auto sm:min-w-[5.5rem]"
                disabled={syncingCategories}
                onClick={() => setCategoryMappingModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary w-full sm:w-auto min-h-[2.5rem] px-5 font-semibold shadow-sm disabled:shadow-none"
                disabled={syncingCategories}
                onClick={() => void confirmCategoryMappingSync()}
              >
                {syncingCategories ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                    Updating…
                  </span>
                ) : (
                  'Update mappings'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
